import { atom, selector } from 'recoil'
import turfCircle from '@turf/circle'
import { Polygon, Position } from 'src/common-services'
import { Bounds, Circle } from 'src/types/map'
import { toLatLng, toPosition } from 'src/utils/map'
import { metersToMiles, sqMToMiles } from 'src/utils/convert'
import { keyFactory, uncachedSelector } from 'src/utils/recoil'
import turf from 'src/utils/turf'

export type DrawFeatureType = 'circle' | 'polygon'
export type DrawMode = DrawFeatureType | 'none'

const key = keyFactory('custom-area')

export const drawMode = atom<DrawMode>({
  key: key('draw-mode'),
  default: 'none',
})

export const polygon = atom<Polygon | undefined>({
  key: key('polygon'),
  default: undefined,
})

export const circle = atom<Circle | undefined>({
  key: key('circle'),
  default: undefined,
})

export const isDrawingActive = selector<boolean>({
  key: key('is-drawing-active'),
  get: ({ get }) => {
    const mode = get(drawMode)
    return mode !== 'none'
  },
})

export const circleFeature = selector<GeoJSON.Feature<GeoJSON.Polygon> | undefined>({
  key: key('circle-feature'),
  get: ({ get }) => {
    const circleObj = get(circle)
    if (!circleObj) {
      return undefined
    }
    const radiusInKm = circleObj.radiusMeters / 1000
    const circleFeature = turfCircle(circleObj.center, radiusInKm, { steps: 300 })
    circleFeature.properties = {
      isCircle: true,
      center: circleObj.center,
      radiusInKm,
    }
    return circleFeature
  },
})

export const polygonError = atom<string | undefined>({
  key: key('polygon-error'),
  default: undefined,
})

/**
 * Returns the geoJSON representation of the area ("circle" or polygon)
 */
export const geoJSON = uncachedSelector<Polygon>({
  key: key('geo-json'),
  get: ({ get }) => {
    const c = get(circle)
    if (c) {
      const { center, radiusMeters } = c
      return turf.circle(center, radiusMeters, { units: 'meters' }).geometry
    }
    const poly = get(polygon)
    if (!poly) {
      throw new Error('cannot get geoJSON with no custom area selected')
    }
    return poly
  },
})

export const hasCustomArea = selector<boolean>({
  key: key('has-custom-area'),
  get: ({ get }) => Boolean(get(polygon) || get(circle)),
  set: ({ set }, newValue) => {
    if (!newValue) {
      set(drawMode, 'none')
      set(circle, undefined)
      set(polygon, undefined)
    }
  },
})

export const areaSqMi = uncachedSelector<number | undefined>({
  key: key('area'),
  get: ({ get }) => {
    const c = get(circle)
    if (c) {
      const miles = metersToMiles(c.radiusMeters)
      return Math.PI * miles * miles
    }
    const poly = get(polygon)
    if (poly) {
      return sqMToMiles(turf.area(poly))
    }
    return undefined
  },
})

export const perimeterMiles = uncachedSelector<number | undefined>({
  key: key('permiter'),
  get: ({ get }) => {
    const c = get(circle)
    if (c) {
      const miles = metersToMiles(c.radiusMeters)
      return Math.PI * 2 * miles
    }
    const poly = get(polygon)
    if (poly) {
      return turf.length(turf.polygonToLine(poly))
    }
    return undefined
  },
})

export const radiusMiles = uncachedSelector<number | undefined>({
  key: key('radius-miles'),
  get: ({ get }) => {
    const c = get(circle)
    return c ? metersToMiles(c.radiusMeters) : undefined
  },
})

export const centroid = uncachedSelector<Position | undefined>({
  key: key('centroid'),
  get: ({ get }) => {
    const c = get(circle)
    if (c) {
      return c.center
    }
    const poly = get(polygon)
    if (poly) {
      return turf.centroid(poly).geometry.coordinates
    }
    return undefined
  },
})

export const bounds = uncachedSelector<Bounds>({
  key: key('bounds'),
  get: ({ get }) => {
    const geo = get(geoJSON)
    const center = get(centroid)
    const googleBounds = new google.maps.LatLngBounds()

    for (const point of geo.coordinates[0]) {
      googleBounds.extend(toLatLng(point))
    }
    return {
      northEast: toPosition(googleBounds.getNorthEast()),
      center: center ?? [0, 0],
      southWest: toPosition(googleBounds.getSouthWest()),
    }
  },
})
